// WebSocket client: connects to the game server on the page's host, sends the
// authenticated hello (identity comes from the session cookie set at sign-in),
// exponential-backoff reconnect, and routes all server messages into the
// GameState via applyServer.

import { WS_SUPERSEDED, decode, encode } from '../shared/protocol'
import type { ClientMsg, ServerMsg } from '../shared/protocol'
import { BUILD_ID } from '../shared/version'
import { applyServer } from './state'
import type { GameState } from './state'

const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 10000
/** A welcome followed by a close sooner than this reads as a server kick, not a real session. */
const QUICK_KICK_MS = 3000
/** Consecutive quick kicks after which we assume another tab holds the session and stop hammering. */
const MAX_QUICK_KICKS = 3

export function connectNet(st: GameState): { send(msg: ClientMsg): void } {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  const observeTok = new URLSearchParams(location.search).get('observe')
  const url = `${proto}//${location.host}${observeTok ? `/?observe=${encodeURIComponent(observeTok)}` : ''}`

  let ws: WebSocket | null = null
  let attempts = 0
  /** When the in-flight RTT ping left (0 = none) — resolved by the next pong. */
  let pingAt = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** True once a newer tab/device took over this account — we stop reconnecting for good. */
  let superseded = false
  /** When the current socket received its welcome (0 = none yet). */
  let welcomeAt = 0
  /** Consecutive "welcomed then immediately kicked" cycles — the two-tab livelock signature. */
  let quickKicks = 0

  function scheduleReconnect(): void {
    if (reconnectTimer !== null) return
    const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** attempts)
    attempts++
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, delay)
  }

  function connect(): void {
    if (superseded) return // another tab owns the session; don't fight it
    let sock: WebSocket
    try {
      sock = new WebSocket(url)
    } catch {
      scheduleReconnect()
      return
    }
    ws = sock

    sock.onopen = () => {
      // Identity rides on the session cookie sent with the WS upgrade; the name
      // here is a harmless placeholder (the server names the faction from the
      // signed-in account).
      sock.send(encode({ t: 'hello', name: 'Settler', build: BUILD_ID }))
    }

    sock.onmessage = (ev) => {
      if (ws !== sock) return
      const msg = decode<ServerMsg>(String(ev.data))
      if (!msg || typeof msg !== 'object' || typeof (msg as { t?: unknown }).t !== 'string') return
      if (msg.t === 'pong') {
        if (pingAt > 0) {
          st.net.rttMs = Math.round(Date.now() - pingAt)
          pingAt = 0
        }
        return
      }
      if (msg.t === 'welcome') {
        welcomeAt = Date.now()
        // Only reset the backoff once we trust this session isn't being kicked
        // right back out. During a two-tab livelock the welcome keeps arriving,
        // so resetting here would keep the reconnect loop pinned at ~1s forever.
        if (quickKicks < MAX_QUICK_KICKS) attempts = 0
        if (!st.connected) {
          st.connected = true
          st.emit('connection', true)
        }
      }
      applyServer(st, msg)
    }

    sock.onerror = () => {
      // onclose always follows; just make sure the socket is torn down.
      if (ws === sock) sock.close()
    }

    sock.onclose = (ev) => {
      if (ws !== sock) return
      ws = null
      if (st.connected) {
        st.connected = false
        st.emit('connection', false)
      }
      // A newer tab/device took over this account: stop reconnecting for good and
      // show the "playing elsewhere" gate. This is what breaks the two-tab livelock.
      if (ev.code === WS_SUPERSEDED) {
        superseded = true
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer)
          reconnectTimer = null
        }
        st.emit('superseded')
        return
      }
      // Classify the drop. A welcome quickly followed by a close is the server
      // kicking us because another socket (another tab) claimed the session;
      // a close after a stable session is an ordinary network drop.
      const wasWelcomed = welcomeAt !== 0
      const kicked = wasWelcomed && Date.now() - welcomeAt < QUICK_KICK_MS
      welcomeAt = 0
      if (kicked) {
        quickKicks++
        if (quickKicks === MAX_QUICK_KICKS) {
          // Stop hammering: surface the conflict and let the backoff escalate so
          // the two tabs no longer trade the session ~once a second.
          st.emit('error', 'Another tab or device is playing this account — reconnecting slowly.')
          st.emit('sessionConflict', true)
        }
      } else if (wasWelcomed) {
        // A session that stayed up past the kick window then dropped is an
        // ordinary network blip — clear the livelock counter so the next welcome
        // resets the backoff for a fast reconnect. (A never-welcomed close, e.g.
        // server down, leaves attempts growing so the backoff still escalates.)
        quickKicks = 0
      }
      scheduleReconnect()
    }
  }

  connect()

  // RTT probe: one ping every 10s (answered by the server's pong). Cheap, and
  // it keeps st.net.rttMs honest for the lag chip and diagnostics.
  setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN && pingAt === 0) {
      pingAt = Date.now()
      ws.send(encode({ t: 'ping' }))
    }
  }, 10_000)

  return {
    send(msg: ClientMsg): void {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(encode(msg))
      // else: silently drop — callers may fire during a reconnect window.
    },
  }
}
